// CreateAdminModal.js
import React, { useState, useEffect } from 'react';
import './CreateAdminModal.css';
import { createAdmin, getRoles } from '../api'; // adjust path as needed


const CreateAdminModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    role: ''
  });
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Fetch roles when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchRoles = async () => {
        setLoadingRoles(true);
        try {
          const fetchedRoles = await getRoles();
          setRoles(fetchedRoles);
          // Set default role if available
          if (fetchedRoles.length > 0 && !formData.role) {
    setFormData(prev => ({
      ...prev,
              role: fetchedRoles[0].value || fetchedRoles[0].name
            }));
          }
        } catch (error) {
          console.error("Failed to fetch roles:", error);
          // Set default roles on error
          setRoles([
            { id: 'admin', name: 'Admin', value: 'Admin' },
            { id: 'super_admin', name: 'Super Admin', value: 'Super Admin' }
          ]);
        } finally {
          setLoadingRoles(false);
        }
      };
      fetchRoles();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    
    if (!formData.role) {
      setSubmitError('Please select a role.');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await createAdmin(formData);
      onSubmit?.(response?.data || response);
      onClose();
      // Reset form
      setFormData({
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        role: roles.length > 0 ? (roles[0].value || roles[0].name) : ''
      });
    } catch (error) {
      console.error("❌ Failed to create admin:", error);
      setSubmitError(error?.message || error?.error || "Failed to create admin. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Admin</h2>
          <button className="close-button" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First name</label>
              <input
                type="text"
                id="firstName"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="middleName">Middle name</label>
              <input
                type="text"
                id="middleName"
                value={formData.middleName}
                onChange={(e) => handleInputChange('middleName', e.target.value)}
                placeholder="Middle name"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="lastName">Last name</label>
              <input
                type="text"
                id="lastName"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email address</label>
              <div className="input-with-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="input-icon">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="role">Role</label>
              <select
                id="role"
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                required
                disabled={loadingRoles}
              >
                {loadingRoles ? (
                  <option value="">Loading roles...</option>
                ) : roles.length === 0 ? (
                  <option value="">No roles available</option>
                ) : (
                  <>
                    <option value="">Select a role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id || role.value || role.name}>
                        {role.name}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>
          </div>

          <div className="modal-actions">
            <button type="submit" className="btn-create-account" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create account'}
            </button>
          </div>
          {submitError && <div className="form-feedback">{submitError}</div>}
        </form>
      </div>
    </div>
  );
};

export default CreateAdminModal; 
